import moment from 'moment-jalaali';

import {
  CalendarDay,
  CalendarDaySlot,
  DayOfWeek,
  GetCalendarManagementResponse,
  SlotStatus,
} from '@models/clinic-list';
import { ActiveTabEnum } from '@models/in-person-booking';

export type BookingDay = {
  date: string;
  dayOfWeek: DayOfWeek;
  totalCapacity: number;
  isFullyBooked: boolean;
  isHolidayBookingDisabled: boolean;
  slots: CalendarDaySlot[];
};

export type BookingTimeSlot = {
  key: string;
  slotId: number;
  medicalCenterId: number;
  startTime: string;
  endTime: string;
  availableCapacity: number;
  isAvailable: boolean;
};

export const getTabForStartTime = (startTime: string): ActiveTabEnum => {
  const hour = parseInt(startTime.split(':')[0], 10);

  if (hour < 12) return ActiveTabEnum.MORNING;
  if (hour < 18) return ActiveTabEnum.NOON;
  return ActiveTabEnum.NIGHT;
};

export const formatTimeSlot = (time: string) => {
  const [hour, minute] = time.split(':');
  return `${Number(hour)}:${minute}`;
};

const isSlotAvailable = (slot: CalendarDaySlot) =>
  slot.status === SlotStatus.Available && slot.availableCapacity > 0;

const isDayFullyBooked = (day: CalendarDay) => {
  if (!day.slots.length || day.availableCapacity <= 0) return true;

  return day.slots.every((slot) => !isSlotAvailable(slot));
};

const isHolidayBookingDisabled = (
  day: CalendarDay,
  includeOfficialHolidaysByScheduleId: Map<number, boolean>,
) => {
  if (!day.isOfficialHoliday) return false;

  return day.slots.some(
    (slot) =>
      includeOfficialHolidaysByScheduleId.get(slot.scheduleId) === false,
  );
};

export const groupCalendarSlotsByDate = (
  calendar?: GetCalendarManagementResponse | null,
): BookingDay[] => {
  if (!calendar?.days?.length) return [];

  const includeOfficialHolidaysByScheduleId = new Map(
    (calendar.schedules ?? []).map((schedule) => [
      schedule.id,
      schedule.includeOfficialHolidays,
    ]),
  );

  return [...calendar.days]
    .map((day) => {
      const holidayBookingDisabled = isHolidayBookingDisabled(
        day,
        includeOfficialHolidaysByScheduleId,
      );

      return {
        date: day.date,
        dayOfWeek: day.dayOfWeek,
        totalCapacity: holidayBookingDisabled ? 0 : day.availableCapacity,
        isFullyBooked: holidayBookingDisabled || isDayFullyBooked(day),
        isHolidayBookingDisabled: holidayBookingDisabled,
        slots: day.slots,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const getTimeSlotsForDay = (
  day?: BookingDay | null,
): BookingTimeSlot[] => {
  if (!day?.slots?.length) return [];

  return [...day.slots]
    .map((slot) => ({
      key: String(slot.id),
      slotId: slot.id,
      medicalCenterId: slot.medicalCenterId,
      startTime: slot.startTime,
      endTime: slot.endTime,
      availableCapacity: day.isHolidayBookingDisabled
        ? 0
        : slot.availableCapacity,
      isAvailable: !day.isHolidayBookingDisabled && isSlotAvailable(slot),
    }))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
};

export const categorizeTimeSlots = (timeSlots: BookingTimeSlot[]) => {
  const result: Record<ActiveTabEnum, BookingTimeSlot[]> = {
    [ActiveTabEnum.MORNING]: [],
    [ActiveTabEnum.NOON]: [],
    [ActiveTabEnum.NIGHT]: [],
  };

  timeSlots.forEach((slot) => {
    result[getTabForStartTime(slot.startTime)].push(slot);
  });

  return result;
};

export const findFirstAvailableDay = (days: BookingDay[]) => {
  const today = moment().startOf('day');

  return (
    days.find((day) => {
      const dayDate = moment(day.date).startOf('day');
      return (
        !dayDate.isBefore(today) && !day.isFullyBooked && day.totalCapacity > 0
      );
    }) ?? null
  );
};
